import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Brain, CheckCircle, EyeOff, Clock } from "lucide-react";

export default function SuggestionsPage() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("OPEN");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/suggestions", { params: { status: filter || undefined } });
        setSuggestions(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [filter]);

  const updateSuggestion = async (id, status) => {
    try {
      await api.put(`/suggestions/${id}`, { status });
      const { data } = await api.get("/suggestions", { params: { status: filter || undefined } });
      setSuggestions(data);
    } catch (e) { console.error(e); }
  };

  const severityColor = (s) => {
    if (s === "critical") return "bg-[#991B1B]/10 text-[#991B1B]";
    if (s === "warn") return "bg-[#92400E]/10 text-[#92400E]";
    return "bg-[#1E40AF]/10 text-[#1E40AF]";
  };

  return (
    <div className="space-y-6" data-testid="suggestions-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={24} className="text-[#C8A961]" />
          <div>
            <h1 className="font-display text-2xl font-semibold text-[#0B1020]">AI Suggestions</h1>
            <p className="text-sm text-[#6B7280] mt-1">AI-driven asset recommendations and trend alerts</p>
          </div>
        </div>
        <select className="stratum-input" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="suggestion-filter">
          <option value="OPEN">Open</option>
          <option value="APPLIED">Applied</option>
          <option value="DISMISSED">Dismissed</option>
          <option value="">All</option>
        </select>
      </div>

      {loading ? (
        <div className="stratum-card p-8 text-center text-[#6B7280]">Loading...</div>
      ) : suggestions.length === 0 ? (
        <div className="stratum-card p-12 text-center">
          <Brain size={40} className="mx-auto text-[#C8A961] mb-4" />
          <h3 className="font-display text-lg font-semibold text-[#0B1020] mb-2">No Suggestions Yet</h3>
          <p className="text-[#6B7280] max-w-md mx-auto">Stratum's AI engine analyses your assets nightly for utilisation trends, ROA anomalies, maintenance patterns, and useful-life indicators. Suggestions will appear here as they are generated.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {suggestions.map(s => (
            <div key={s.id} className="stratum-card p-5 hover:shadow-sm transition-shadow" data-testid={`suggestion-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${severityColor(s.severity)}`}>{s.severity}</span>
                    <span className="text-xs text-[#6B7280] uppercase tracking-wider">{s.type?.replace("_", " ")}</span>
                  </div>
                  <h4 className="font-medium text-[#0B1020]">{s.headline}</h4>
                  <p className="text-sm text-[#6B7280] mt-1">{s.rationale}</p>
                </div>
                {s.status === "OPEN" && (
                  <div className="flex gap-2 ml-4">
                    <button onClick={() => updateSuggestion(s.id, "APPLIED")} className="px-3 py-1.5 text-xs rounded-lg bg-[#065F46]/10 text-[#065F46] hover:bg-[#065F46]/20" data-testid={`apply-suggestion-${s.id}`}>Apply</button>
                    <button onClick={() => updateSuggestion(s.id, "DISMISSED")} className="px-3 py-1.5 text-xs rounded-lg bg-[#6B7280]/10 text-[#6B7280] hover:bg-[#6B7280]/20" data-testid={`dismiss-suggestion-${s.id}`}>Dismiss</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
